import { VENUE_RATINGS_ENABLED } from "../../config/meetingLocationFeatures";
import VenueRating from "../../components/meetingLocation/VenueRating";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeft, Heart, X, Loader2 } from "lucide-react";
import "./TeamPawsWalkFeedbackPage.css";
import apiClient from "../../api/apiClient";
import { useGlobalAlert } from "../../context/AlertContext";

import tablerBone from "../../assets/tabler_bone.png";
import dogLevel0 from "../../assets/dogLevel5.png";
import dogLevel1 from "../../assets/dogLevel2.png";
import dogLevel2 from "../../assets/dogLevel4.png";

import dogHeadLevel0 from "../../assets/dogLevel1.png";
import dogHeadLevel1 from "../../assets/dogLevel2.png";
import dogHeadLevel2 from "../../assets/dogLevel3.png";
import dogHeadLevel3 from "../../assets/dogLevel4.png";

import TopNavBar from "../../components/TopNavBar";
import dogPlaceholder from "../../assets/dogPlaceholder.png";
import userPlaceholder from "../../assets/userPlaceholder.png";
import appIcon from "../../assets/appIcon.svg";
import MobileTopBar from "../../components/MobileTopBar";

const TeamPawsWalkFeedbackPage = () => {
  const navigate = useNavigate();
  const { triggerAlert } = useGlobalAlert();

  const location = useLocation();
  const { profile } = useSelector((state) => state.user);
  const [user, setUser] = useState(null);
  const [partner, setPartner] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const res = await apiClient.get(`/user/details/${profile?.id}`);
        if (res.data?.status && res.data.data) setUser(res.data.data);
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    if (profile?.id) fetchUserDetails();
  }, [profile?.id]);

  const myDogName = user?.dogs?.[0]?.dog_name || "My Dog";
  const userDogStatus = user?.user_dog_status || 0;

  const bookingData = location.state || {};
  const { bookingId, partnerId, partnerName, partnerAvatar, isRequester } =
    bookingData;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [milestone, setMilestone] = useState(0);

  useEffect(() => {
    const fetchPartnerDetails = async () => {
      try {
        const res = await apiClient.get(`/user/details/${partnerId}`);
        if (res.data?.status && res.data.data) setPartner(res.data.data);
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    if (partnerId) fetchPartnerDetails();
  }, [partnerId]);

  const partnerDogStatus = partner?.user_dog_status || 0;

  useEffect(() => {
    const fetchMilestones = async () => {
      if (!profile?.id || !partnerId) return;

      try {
        const payload = {
          requester_user_id: isRequester ? profile.id : partnerId,
          walker_user_id: isRequester ? partnerId : profile.id,
        };

        const res = await apiClient.post(
          "/milestone/get-requester-and-walker-milestones",
          payload,
        );

        if (res.data?.status && res.data.data) {
          const { requester, walker } = res.data.data;

          const partnerData =
            requester.user_id !== profile.id ? requester : walker;
          setMilestone(partnerData.milestone);

          setJourneyData((prev) =>
            prev.map((u) => ({
              ...u,
              progress: partnerData.milestone,
            })),
          );
        }
      } catch (err) {
        console.error("Error fetching milestones:", err);
      }
    };

    fetchMilestones();
  }, [profile?.id, partnerId]);

  const feedbackSliderRef = useRef(null);
  const journeySliderRefs = useRef({});
  const isDraggingJourney = useRef(false);
  const draggingUserId = useRef(null);
  const isDraggingFeedback = useRef(false);

  const [sliderValue, setSliderValue] = useState(1);
  const [viewState, setViewState] = useState(0);
  const [showTreatModal, setShowTreatModal] = useState(false);

  const feedbackOptions = [
    "I'd like to drop the leash\non this connection",
    "I'd like to keep sniffing\nout this connection",
    userDogStatus === 0
      ? 'Yes, I am ready to\n"Take the leash!"'
      : 'Yes, I am ready to\n"Pass the leash!"',
  ];

  const SLIDER_START = 12; // %
  const SLIDER_END = 88; // %
  const SLIDER_RANGE = SLIDER_END - SLIDER_START; // 76%
  const STEP_COUNT = 2; // 3 steps → 2 intervals
  const STEP_PERCENT = SLIDER_RANGE / STEP_COUNT; // 38%

  const thumbPosition = SLIDER_START + sliderValue * STEP_PERCENT;

  const dogLevelImages = {
    0: dogLevel0,
    1: dogLevel1,
    2: dogLevel2,
  };

  const dogIcons = [dogHeadLevel0, dogHeadLevel1, dogHeadLevel2, dogHeadLevel3];

  const [journeyData, setJourneyData] = useState([
    { id: 1, name: "user", avatar: userPlaceholder, progress: 0 },
  ]);

  const TRACK_START = 5;
  const TRACK_END = 95;
  const TRACK_RANGE = TRACK_END - TRACK_START;

  const getFeedbackStepFromX = (clientX) => {
    const rect = feedbackSliderRef.current.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    const percent = (x / rect.width) * 100;

    const raw = Math.round((percent - SLIDER_START) / STEP_PERCENT);
    return Math.min(Math.max(raw, 0), 2);
  };

  const getJourneyStepFromX = (clientX, rect) => {
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    const percent = (x / rect.width) * 100;

    const clamped = Math.min(TRACK_END, Math.max(TRACK_START, percent));
    const normalized = (clamped - TRACK_START) / TRACK_RANGE;

    return Math.round(normalized * 3); // 0–3
  };

  const startFeedbackDrag = (e) => {
    if (e?.preventDefault) e.preventDefault();
    isDraggingFeedback.current = true;

    document.addEventListener("mousemove", moveFeedbackDrag);
    document.addEventListener("mouseup", endFeedbackDrag);
    document.addEventListener("touchmove", moveFeedbackDrag, {
      passive: false,
    });
    document.addEventListener("touchend", endFeedbackDrag);
  };

  const moveFeedbackDrag = (e) => {
    if (!isDraggingFeedback.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    if (clientX == null) return;

    setSliderValue(getFeedbackStepFromX(clientX));
  };

  const endFeedbackDrag = () => {
    isDraggingFeedback.current = false;

    document.removeEventListener("mousemove", moveFeedbackDrag);
    document.removeEventListener("mouseup", endFeedbackDrag);
    document.removeEventListener("touchmove", moveFeedbackDrag);
    document.removeEventListener("touchend", endFeedbackDrag);
  };

  const moveJourneyDrag = (e) => {
    if (!isDraggingJourney.current || !draggingUserId.current) return;

    const slider = journeySliderRefs.current[draggingUserId.current];
    if (!slider) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    if (clientX == null) return;

    const rect = slider.getBoundingClientRect();
    updateProgress(draggingUserId.current, getJourneyStepFromX(clientX, rect));
  };

  const endJourneyDrag = () => {
    isDraggingJourney.current = false;
    draggingUserId.current = null;

    document.removeEventListener("mousemove", moveJourneyDrag);
    document.removeEventListener("mouseup", endJourneyDrag);
    document.removeEventListener("touchmove", moveJourneyDrag);
    document.removeEventListener("touchend", endJourneyDrag);
  };

  const updateProgress = (userId, step) => {
    setJourneyData((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, progress: step } : u)),
    );
  };

  const handleSubmit = async () => {
    if (!profile?.id || !bookingId) {
      triggerAlert("Missing profile or booking information.", "error");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      user_id: profile.id,
      booking_id: bookingId,
      review: feedbackOptions[sliderValue].replace("\n", " "),
      review_level: sliderValue,
    };

    try {
      const response = await apiClient.post(
        "/rateWalk/create-rate-walk",
        payload,
      );

      if (response.data?.status) {
        const milestonePayload = {
          requester_user_id: isRequester ? profile.id : partnerId,
          walker_user_id: isRequester ? partnerId : profile.id,
        };

        const milRes = await apiClient.post(
          "/milestone/get-requester-and-walker-milestones",
          milestonePayload,
        );

        if (milRes.data?.status && milRes.data.data) {
          const { requester, walker } = milRes.data.data;

          const myMilestoneData =
            requester.user_id === profile.id ? requester : walker;
          const partnerMilestoneData =
            requester.user_id === profile.id ? walker : requester;

          if (
            myMilestoneData.milestone < 2 &&
            partnerMilestoneData.milestone >= 2
          ) {
            setViewState(1);
            setIsSubmitting(false);
            return;
          }
        }

        if (sliderValue === 1) {
          triggerAlert("Feedback submitted successfully.", "success");
          navigate("/current-bookings", { state: { returnId: bookingId } });
        } else if (sliderValue === 2) {
          if (milestone >= 2) {
            setViewState(2);
          } else {
            triggerAlert("Feedback submitted successfully.", "success");
            navigate("/current-bookings", { state: { returnId: bookingId } });
          }
        } else {
          triggerAlert("Feedback submitted successfully.", "success");
          navigate("/current-bookings", { state: { returnId: bookingId } });
        }
      } else {
        triggerAlert(
          response.data?.message || "Failed to submit rating.",
          "error",
        );
      }
    } catch (error) {
      console.error("Rate API Error:", error);
      triggerAlert("Something went wrong. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate("/current-bookings", { state: { returnId: bookingId } });
  };

  const handleGoToAvailabilityPage = () => {
    // const canNavigate = milestone >= 2 && userDogStatus !== partnerDogStatus;
    const canNavigate = milestone >= 2;

    if (!canNavigate) {
      return triggerAlert(
        "Availability features are not available for this booking yet.",
        "error",
      );
    }

    navigate("/team-paws-availability", {
      state: {
        partnerId,
        partnerName,
        bookingId,
      },
    });
  };

  const handleGoToPupCarePlayBookPage = () => {
    navigate("/walkies-and-food");
  };

  const handleGoToCurrentBookingViewPage = () => {
    navigate("/current-bookings", { state: { returnId: bookingId } });
  };

  const handleGoToProfile = (dog) => {
    navigate("/profile-details", {
      state: { dogData: dog },
    });
  };

  const handleGoTorateBuddyPage = () => {
    navigate("/walk-buddy-rating", {
      state: {
        bookingId: bookingId,
        partnerId: partnerId,
        partnerName: partnerName,
        partnerAvatar: partnerAvatar,
        isRequester: isRequester,
      },
    });
  };

  return (
    <div className="feedback-container">
      {/* DESKTOP HEADER */}
      <div className="feedback-top-nav tp-desktop-only">
        <button className="back-nav-btn" onClick={handleBack}>
          <ArrowLeft size={24} color="#000" strokeWidth={3} />
          <span style={{ color: "rgba(0, 0, 0, 0.5)" }}>Back</span>
        </button>

        <div className="header-icons-right">
          <TopNavBar theme="light" />
        </div>
      </div>

      {/* MOBILE HEADER */}
      <div className="feedback-top-nav-mobile tp-mobile-only">
        {/* <div className="feedback-header-top-row">
          <button className="back-nav-btn-mobile" onClick={handleBack}>
            <ArrowLeft size={24} color="#fff" strokeWidth={3} />
            <span>Back</span>
          </button>

          <div
            className="feedback-custom-brand"
            onClick={() => navigate("/dashboard-switcher")}
          >
            <img src={appIcon} alt="Social Paws" style={{ width: "22px" }} />
            <span>Social Paws</span>
          </div>
        </div> */}
        <MobileTopBar onBack={handleBack} backColor="#fff" logoColor="#fff" />

        <div className="feedback-header-bottom-row">
          <TopNavBar theme="dark" />
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="feedback-content">
        {/* TITLE */}
        <h1 className="feedback-title">
          {viewState === 0 &&
            `HOW WAS YOUR WALK\nWITH ${partnerName?.toUpperCase()}?`}

          {viewState === 1 && (
            <>
              {partnerName?.toUpperCase()} is ready to{" "}
              {partnerDogStatus === 0 ? "TAKE THE LEASH" : "PASS THE LEASH"}!
              <br />
              AND you are not,
              <br />
              LET’S book another group walk
            </>
          )}

          {viewState === 2 && userDogStatus === 1 && milestone === 2 && (
            <>
              CONGRATULATIONS!
              <br />
              YOU HAVE UNLOCKED MILESTONE {milestone}:
              <br />
              PASSING THE LEASH TO {partnerName?.toUpperCase()}
            </>
          )}

          {viewState === 2 && userDogStatus === 0 && milestone === 2 && (
            <>
              CONGRATULATIONS!
              <br />
              YOU HAVE UNLOCKED MILESTONE {milestone}:
              <br />
              TAKING THE LEASH WITH {partner.dogs?.[0]?.dog_name?.toUpperCase()}
            </>
          )}

          {viewState === 2 && milestone === 3 && (
            <>
              CONGRATULATIONS!
              <br />
              YOU HAVE REACHED THE FINAL MILESTONE OF
              <br />
              YOUR DOG-SHARING JOURNEY:
              <br />
              FREELY DOG-SHARING
            </>
          )}
        </h1>

        {/* PROFILE CARD (hidden only in state 3) */}
        {viewState !== 2 && (
          <div className="feedback-profile-card">
            <img
              src={
                partnerAvatar ||
                (partnerDogStatus === 0 ? userPlaceholder : dogPlaceholder)
              }
              alt={partnerName}
              className="profile-img"
            />
            <div className="profile-overlay">
              <div className="profile-info-row">
                <Heart size={26} strokeWidth={2} />
                <span className="profile-name">
                  {partnerName?.toUpperCase()}
                </span>
                <span className="profile-dist">2.5mi away</span>
              </div>
            </div>
          </div>
        )}

        {viewState === 2 && userDogStatus === 1 && (
          <div className="journey-list" style={{ paddingBottom: "3rem" }}>
            {journeyData.map((user) => {
              const positionPercent =
                TRACK_START + (user.progress / 3) * TRACK_RANGE;

              return (
                <div key={user.id} className="journey-row">
                  <div className="journey-user-avatar tp-desktop-only">
                    <img
                      src={
                        partnerAvatar ||
                        (partnerDogStatus === 0
                          ? userPlaceholder
                          : dogPlaceholder)
                      }
                      alt={user.name}
                    />
                  </div>

                  <div className="journey-slider-container">
                    <div
                      className="journey-track-bg"
                      style={{ height: "12px", borderRadius: "6px" }}
                    />
                    <div
                      className="journey-track-fill"
                      style={{
                        width: `${positionPercent}%`,
                        height: "12px",
                        borderRadius: "6px",
                      }}
                    />

                    {[0, 1, 2, 3].map((step) => (
                      <div
                        key={step}
                        className="journey-dot"
                        style={{
                          left: `${TRACK_START + (step / 3) * TRACK_RANGE}%`,
                          width: "10px",
                          height: "10px",
                          cursor: "default",
                        }}
                      />
                    ))}

                    <div
                      className="journey-dog-thumb"
                      style={{
                        left: `${positionPercent}%`,
                        cursor: "default",
                      }}
                    >
                      <img
                        src={dogIcons[user.progress]}
                        alt="Dog level"
                        style={{
                          width: user.progress >= 2 ? "70px" : "62px",
                          height: user.progress >= 2 ? "70px" : "62px",
                          transform: "translate(0%, -15%)",
                        }}
                      />
                    </div>

                    <div
                      className="journey-labels-row"
                      style={{ paddingTop: "1rem" }}
                    >
                      {[
                        "TOSS TREATS",
                        "MILESTONE 1",
                        "MILESTONE 2",
                        "MILESTONE 3",
                      ].map((label, i) => (
                        <span
                          key={i}
                          className={user.progress === i ? "active" : ""}
                          style={{
                            color: "#fff",
                            cursor: "default",
                            fontSize: isMobile ? "11px" : "",
                          }}
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* QUESTION TEXT */}
        <p
          className={`feedback-question ${
            viewState === 2 ? "feedback-question--state-2" : ""
          }`}
        >
          {viewState === 0 &&
            (userDogStatus === 0
              ? `Are you comfortable "Taking the leash" with ${partnerName}?`
              : `Are you comfortable "Passing the leash" to ${partnerName}?`)}

          {viewState === 1 && `Book your next walk with ${partnerName}!`}

          {viewState === 2 && userDogStatus === 1 && (
            <>
              NEXT:
              <br />
              Offer {myDogName}’s availability for Solo Time and complete his
              Pup Care Playbook
            </>
          )}

          {viewState === 2 && userDogStatus === 0 && milestone === 2 && (
            <>
              Book your next walk or playdate via the booking button in{" "}
              {partner.dogs?.[0].dog_name}’s profile!
            </>
          )}

          {viewState === 2 && userDogStatus === 1 && milestone === 3 && (
            <>
              We will share your choice with {myDogName} & {profile.user_name}{" "}
              to move you all forward on <br /> your dog-sharing journey!
            </>
          )}

          {viewState === 2 && userDogStatus === 0 && milestone === 3 && (
            <>
              We will share your choice with {partner.dogs?.[0]?.dog_name} &{" "}
              {partner.user_name} to move you all forward on <br /> your
              dog-sharing journey!
            </>
          )}
        </p>

        {/* SLIDER (ONLY INITIAL STATE) */}
        {viewState === 0 && (
          <div className="feedback-slider-area" ref={feedbackSliderRef}>
            <div className="slider-track-container-tpw">
              <div className="slider-track-bg-tpw"></div>
              <div
                className="slider-track-fill-tpw"
                style={{
                  width: `${thumbPosition - SLIDER_START + SLIDER_START}%`,
                }}
              />

              {[0, 1, 2].map((step) => (
                <div
                  key={step}
                  className="slider-dot-wrapper"
                  style={{
                    left: `${SLIDER_START + step * STEP_PERCENT}%`,
                  }}
                  onClick={() => setSliderValue(step)}
                >
                  <div
                    className={`slider-dot ${
                      step <= sliderValue ? "active" : ""
                    }`}
                  />
                </div>
              ))}

              <div
                className="slider-thumb"
                style={{ left: `${thumbPosition}%` }}
                onMouseDown={startFeedbackDrag}
                onTouchStart={startFeedbackDrag}
              >
                <img
                  src={dogLevelImages[sliderValue]}
                  alt="Slider Dog"
                  style={{
                    width:
                      sliderValue === 2
                        ? isMobile
                          ? "65px"
                          : "70px"
                        : isMobile
                          ? "45px"
                          : "60px",
                    height:
                      sliderValue === 2
                        ? isMobile
                          ? "60px"
                          : "70px"
                        : isMobile
                          ? "55px"
                          : "60px",
                  }}
                />
              </div>
            </div>

            <div className="slider-labels-row">
              {feedbackOptions.map((text, index) => (
                <div
                  key={index}
                  className={`slider-label-item ${
                    index === sliderValue ? "selected" : ""
                  }`}
                  onClick={() => setSliderValue(index)}
                >
                  {text}
                </div>
              ))}
            </div>
          </div>
        )}

        {VENUE_RATINGS_ENABLED && (
        <VenueRating
          key={`${profile?.id}-${location.state?.location_id ?? "demo"}`}
          userId={profile?.id}
          locationId={location.state?.location_id}
        />
        )}

        {/* BUTTONS */}
        <div className="feedback-buttons-row">
          {viewState === 0 && (
            <>
              <button
                className="feedback-btn outline"
                onClick={() => setShowTreatModal(true)}
              >
                SHARE FEEDBACK
              </button>

              <button
                className="feedback-btn filled"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? <Loader2 className="animate-spin" /> : "SUBMIT"}
              </button>
            </>
          )}

          {viewState === 1 && (
            <button
              className="feedback-btn filled full-width"
              onClick={handleGoToCurrentBookingViewPage}
            >
              OKAY
            </button>
          )}

          {viewState === 2 && userDogStatus === 1 && (
            <>
              <button
                className="feedback-btn filled"
                onClick={handleGoToAvailabilityPage}
              >
                OFFER {myDogName}'S AVAILABILITY
              </button>
              <button
                className="feedback-btn filled"
                onClick={handleGoToPupCarePlayBookPage}
              >
                COMPLETE {myDogName}'S PUP CARE PLAYBOOK
              </button>
            </>
          )}

          {viewState === 2 && userDogStatus === 0 && (
            <>
              <button
                className="feedback-btn filled"
                onClick={() => handleGoToProfile(partner?.dogs?.[0])}
              >
                {partner.dogs?.[0]?.dog_name?.toUpperCase()}'S PROFILE
              </button>
              <button
                className="feedback-btn filled"
                onClick={() => handleGoToProfile(partner?.dogs?.[0])}
              >
                BOOK {partner.dogs?.[0]?.dog_name?.toUpperCase()}
              </button>
            </>
          )}
        </div>
      </div>

      {/* MODAL */}
      {showTreatModal && (
        <div className="treat-modal-overlay">
          <div className="treat-modal-card">
            <button
              className="treat-close-btn"
              onClick={() => setShowTreatModal(false)}
            >
              <X size={16} strokeWidth={4} color="rgba(166, 28, 31, 1)" />
            </button>

            <div className="treat-modal-content">
              <div className="treat-icons-row">
                <div className="cb-treat-dog-avatar">
                  <img
                    src={
                      partnerAvatar ||
                      (partnerDogStatus === 0
                        ? userPlaceholder
                        : dogPlaceholder)
                    }
                    alt="dogIcon"
                  />
                </div>
                <img src={tablerBone} alt="bone" style={{ width: "60px" }} />
              </div>

              <h3 className="treat-high-paw" style={{ marginBottom: "1rem" }}>
                HIGH PAW!
              </h3>

              <h2 style={{ fontSize: "1.5rem" }}>
                We will share your response with{" "}
                {partner.dogs?.[0]?.dog_name || ""} & {partnerName || ""} to
                move you all forward on your dog-sharing journey!
              </h2>
            </div>
            <button
              className="feedback-btn filled"
              style={{
                width: "400px",
                marginTop: "18px",
                alignSelf: "center",
                marginLeft: isMobile ? "0" : "3.5rem",
              }}
              onClick={handleGoTorateBuddyPage}
            >
              RATE YOUR WALK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamPawsWalkFeedbackPage;
